// Request Redemption Dialog
// Allows investors to request redemption of their series
// Implements Loan Agreement Clauses 4.1-4.5

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  Slider,
  ButtonGroup,
} from '@mui/material';
import {
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { Series } from '../../types';
import {
  calculateRedemptionValue,
  validateRedemptionEligibility,
  createRedemptionRequest,
  RedemptionCalculation,
  RedemptionEligibility,
} from '../../services/redemptionService';

interface RequestRedemptionDialogProps {
  open: boolean;
  onClose: () => void;
  series: Series[]; // Available series for the investor
  lenderId: string;
  onSuccess?: () => void;
}

const RequestRedemptionDialog: React.FC<RequestRedemptionDialogProps> = ({
  open,
  onClose,
  series,
  lenderId,
  onSuccess,
}) => {
  const [selectedSeriesId, setSelectedSeriesId] = useState('');
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState('');
  const [eligibility, setEligibility] = useState<RedemptionEligibility | null>(null);
  const [calculation, setCalculation] = useState<RedemptionCalculation | null>(null);
  
  // NEW for Sprint 6B: Partial redemption state
  const [redemptionType, setRedemptionType] = useState<'full' | 'partial'>('full');
  const [redemptionPercentage, setRedemptionPercentage] = useState<number>(100);

  // Filter only active series
  const activeSeries = series.filter(s => s.status === 'active');

  // Reset when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSelectedSeriesId('');
      setEligibility(null);
      setCalculation(null);
      setError('');
      // Reset partial redemption state
      setRedemptionType('full');
      setRedemptionPercentage(100);
    }
  }, [open]);

  // When series is selected OR redemption type/percentage changes, recalculate
  useEffect(() => {
    if (selectedSeriesId) {
      checkEligibilityAndCalculate();
    } else {
      setEligibility(null);
      setCalculation(null);
    }
  }, [selectedSeriesId, redemptionType, redemptionPercentage]);

  const checkEligibilityAndCalculate = async () => {
    setCalculating(true);
    setError('');
    
    try {
      // Check eligibility
      const eligibilityResult = await validateRedemptionEligibility(selectedSeriesId);
      setEligibility(eligibilityResult);
      
      if (!eligibilityResult.canRedeem) {
        setError(eligibilityResult.reason || 'Cannot redeem this series');
        setCalculation(null);
        return;
      }
      
      // Calculate redemption value with partial redemption support
      const selectedSeries = series.find(s => s.id === selectedSeriesId);
      if (!selectedSeries) {
        setError('Series not found');
        return;
      }
      
      // Pass redemption type and percentage to calculation
      const effectivePercentage = redemptionType === 'full' ? 100 : redemptionPercentage;
      const calculationResult = await calculateRedemptionValue(
        selectedSeries,
        undefined, // requestDate (uses default)
        redemptionType,
        effectivePercentage
      );
      setCalculation(calculationResult);
      
    } catch (err: any) {
      setError(err.message || 'Failed to calculate redemption value');
    } finally {
      setCalculating(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedSeriesId) {
      setError('Please select a series');
      return;
    }
    
    if (!calculation) {
      setError('Calculation not available');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // NEW for Sprint 6B: Pass redemption type and percentage
      const effectivePercentage = redemptionType === 'full' ? 100 : redemptionPercentage;
      await createRedemptionRequest(
        selectedSeriesId, 
        lenderId,
        redemptionType,
        effectivePercentage
      );
      
      if (onSuccess) onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create redemption request');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const selectedSeries = series.find(s => s.id === selectedSeriesId);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#1a1a1a',
          border: '1px solid #FFD700',
        },
      }}
    >
      <DialogTitle sx={{ color: '#FFD700', borderBottom: '1px solid #333' }}>
        Request Redemption
        <Typography variant="caption" display="block" sx={{ color: '#999', mt: 0.5 }}>
          Per Loan Agreement Clause 4: Lock-Up Period and Redemption
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Series Selector */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel sx={{ color: '#999' }}>Select Series to Redeem *</InputLabel>
          <Select
            value={selectedSeriesId}
            onChange={(e) => setSelectedSeriesId(e.target.value)}
            label="Select Series to Redeem *"
            disabled={loading || activeSeries.length === 0 || calculating}
            sx={{ color: 'white' }}
          >
            {activeSeries.length === 0 && (
              <MenuItem value="">
                <em>No active series available</em>
              </MenuItem>
            )}
            {activeSeries.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                Series {s.id.slice(0, 8)} - ${s.currentNAV.toLocaleString()} (Principal: ${s.principalAmount.toLocaleString()})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* NEW for Sprint 6B: Redemption Type & Amount Selector */}
        {selectedSeries && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              bgcolor: '#0a0a0a',
              border: '1px solid #FFD700',
            }}
          >
            <Typography variant="subtitle2" sx={{ color: '#FFD700', mb: 2 }}>
              🆕 Redemption Amount (Sprint 6B)
            </Typography>

            {/* Redemption Type Radio Buttons */}
            <RadioGroup
              value={redemptionType}
              onChange={(e) => setRedemptionType(e.target.value as 'full' | 'partial')}
              sx={{ mb: 2 }}
            >
              <FormControlLabel
                value="full"
                control={<Radio sx={{ color: '#FFD700', '&.Mui-checked': { color: '#FFD700' } }} />}
                label={
                  <Box>
                    <Typography variant="body2" sx={{ color: 'white' }}>Full Redemption (100%)</Typography>
                    <Typography variant="caption" sx={{ color: '#999' }}>Redeem entire series and close</Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="partial"
                control={<Radio sx={{ color: '#FFD700', '&.Mui-checked': { color: '#FFD700' } }} />}
                label={
                  <Box>
                    <Typography variant="body2" sx={{ color: 'white' }}>Partial Redemption</Typography>
                    <Typography variant="caption" sx={{ color: '#999' }}>Redeem a percentage, keep series active</Typography>
                  </Box>
                }
              />
            </RadioGroup>

            {/* Percentage Controls (only show for partial) */}
            {redemptionType === 'partial' && (
              <Box>
                <Typography variant="body2" sx={{ color: 'white', mb: 1 }}>
                  Select Redemption Percentage:
                </Typography>

                {/* Preset Buttons */}
                <ButtonGroup variant="outlined" fullWidth sx={{ mb: 2 }}>
                  {[25, 50, 75, 100].map((pct) => (
                    <Button
                      key={pct}
                      onClick={() => setRedemptionPercentage(pct)}
                      sx={{
                        borderColor: redemptionPercentage === pct ? '#FFD700' : '#666',
                        bgcolor: redemptionPercentage === pct ? 'rgba(255, 215, 0, 0.1)' : 'transparent',
                        color: redemptionPercentage === pct ? '#FFD700' : 'white',
                        '&:hover': {
                          borderColor: '#FFD700',
                          bgcolor: 'rgba(255, 215, 0, 0.1)',
                        },
                      }}
                    >
                      {pct}%
                    </Button>
                  ))}
                </ButtonGroup>

                {/* Slider for Custom Percentage */}
                <Box sx={{ px: 1 }}>
                  <Slider
                    value={redemptionPercentage}
                    onChange={(_, value) => setRedemptionPercentage(value as number)}
                    min={10}
                    max={100}
                    step={5}
                    marks={[
                      { value: 10, label: '10%' },
                      { value: 25, label: '25%' },
                      { value: 50, label: '50%' },
                      { value: 75, label: '75%' },
                      { value: 100, label: '100%' },
                    ]}
                    valueLabelDisplay="on"
                    sx={{
                      color: '#FFD700',
                      '& .MuiSlider-valueLabel': {
                        bgcolor: '#FFD700',
                        color: '#000',
                      },
                      '& .MuiSlider-markLabel': {
                        color: '#999',
                      },
                    }}
                  />
                </Box>

                <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 2 }}>
                  💡 Minimum: 10% | You can redeem multiple times from the same series
                </Typography>
              </Box>
            )}
          </Paper>
        )}

        {/* Loading State */}
        {calculating && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#FFD700' }} />
            <Typography sx={{ ml: 2, color: '#999' }}>
              Calculating redemption value...
            </Typography>
          </Box>
        )}

        {/* Series Details & Calculations */}
        {selectedSeries && eligibility && calculation && !calculating && (
          <Box>
            {/* Lock-Up Status Banner */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                bgcolor: eligibility.isWithinLockup ? '#ff4444' : '#4CAF50',
                border: `1px solid ${eligibility.isWithinLockup ? '#ff6666' : '#66BB6A'}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {eligibility.isWithinLockup ? <LockIcon /> : <LockOpenIcon />}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ color: 'white' }}>
                    {eligibility.isWithinLockup ? '🔒 Within Lock-Up Period' : '✅ Lock-Up Expired'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    {eligibility.isWithinLockup
                      ? `Lock-up ends: ${calculation.lockupEnd.toLocaleDateString()} (${eligibility.daysUntilLockupExpiry} days remaining)`
                      : `Lock-up ended: ${calculation.lockupEnd.toLocaleDateString()}`
                    }
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    Clause 4.1: 12-month lock-up period from Issue Date
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Early Redemption Fee Warning */}
            {eligibility.earlyRedemptionFeeApplies && (
              <Alert
                severity="warning"
                icon={<WarningIcon />}
                sx={{
                  mb: 2,
                  bgcolor: 'rgba(255, 183, 77, 0.1)',
                  border: '1px solid #FFB74D',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Early Redemption Fee Applies
                </Typography>
                <Typography variant="body2">
                  Redeeming within the lock-up period incurs a 3% early redemption fee of ${calculation.earlyRedemptionFee.toLocaleString()}.
                </Typography>
                <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                  Clause 4.3: Early redemption fee is 3% of Redemption Value if within lock-up
                </Typography>
              </Alert>
            )}

            <Divider sx={{ my: 2, borderColor: '#333' }} />

            {/* Redemption Timeline */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                bgcolor: '#0a0a0a',
                border: '1px solid #333',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CalendarIcon sx={{ color: '#FFD700', fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ color: '#FFD700' }}>
                  Redemption Timeline
                </Typography>
              </Box>
              
              <Box sx={{ pl: 4 }}>
                <Typography variant="body2" sx={{ color: 'white', mb: 0.5 }}>
                  <strong>Request Date:</strong> {calculation.requestDate.toLocaleDateString()} (Today)
                </Typography>
                <Typography variant="body2" sx={{ color: 'white', mb: 0.5 }}>
                  <strong>Redemption Date:</strong> {calculation.redemptionDate.toLocaleDateString()} (30 days notice)
                </Typography>
                <Typography variant="body2" sx={{ color: 'white' }}>
                  <strong>Payment Due By:</strong> {calculation.paymentDueDate.toLocaleDateString()} (30 days after redemption)
                </Typography>
                <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 1 }}>
                  Clause 4.2: 30-day notice required | Clause 4.5: Payment within 30 days of Redemption Date
                </Typography>
              </Box>
            </Paper>

            {/* Value Breakdown */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                bgcolor: '#0a0a0a',
                border: '1px solid #FFD700',
              }}
            >
              <Typography variant="subtitle2" sx={{ color: '#FFD700', mb: 2 }}>
                Redemption Value Breakdown
              </Typography>

              {/* Base Values */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#999' }}>
                    Trading NAV (from Interactive Brokers)
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    ${calculation.tradingNAV.toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#999' }}>
                    Capitalized Interest (from crystallizations)
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    ${calculation.capitalizedInterest.toLocaleString()}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1, borderColor: '#333' }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>
                    Total Series NAV
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>
                    ${calculation.totalNAV.toLocaleString()}
                  </Typography>
                </Box>
              </Box>

              {/* Deductions */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: '#FFD700', display: 'block', mb: 1 }}>
                  Deductions per Clause 4.4:
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#999' }}>
                    Less: Accrued Performance Fee (20%)
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#ff6666' }}>
                    -${calculation.accruedPerformanceFee.toLocaleString()}
                  </Typography>
                </Box>
                
                {calculation.earlyRedemptionFee > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: '#999' }}>
                      Less: Early Redemption Fee (3%)
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#ff6666' }}>
                      -${calculation.earlyRedemptionFee.toLocaleString()}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 2, borderColor: '#FFD700' }} />

              {/* Final Value */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ color: '#FFD700' }}>
                  Final Redemption Value
                </Typography>
                <Typography variant="h5" sx={{ color: '#FFD700', fontWeight: 'bold' }}>
                  ${calculation.redemptionValue.toLocaleString()}
                </Typography>
              </Box>
              
              <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 1 }}>
                This amount will be paid to your bank account on file within 30 days of the Redemption Date
              </Typography>
            </Paper>

            {/* NEW for Sprint 6B: Remaining Value Preview (for partial redemptions) */}
            {redemptionType === 'partial' && redemptionPercentage < 100 && selectedSeries && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 2,
                  bgcolor: '#0a0a0a',
                  border: '1px solid #4CAF50',
                }}
              >
                <Typography variant="subtitle2" sx={{ color: '#4CAF50', mb: 2 }}>
                  ✨ Remaining in Series After Redemption
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#999' }}>
                    Current Series NAV
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    ${selectedSeries.currentNAV.toLocaleString()}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#999' }}>
                    Redeeming ({redemptionPercentage}%)
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#ff6666' }}>
                    -${(selectedSeries.currentNAV * redemptionPercentage / 100).toLocaleString()}
                  </Typography>
                </Box>

                <Divider sx={{ my: 1, borderColor: '#4CAF50' }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
                    Remaining NAV ({100 - redemptionPercentage}%)
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
                    ${(selectedSeries.currentNAV * (100 - redemptionPercentage) / 100).toLocaleString()}
                  </Typography>
                </Box>

                <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 1 }}>
                  💡 Series will remain ACTIVE. You can redeem more later or wait for further growth!
                </Typography>
              </Paper>
            )}

            {/* Important Notes */}
            <Alert
              severity="info"
              icon={<InfoIcon />}
              sx={{
                bgcolor: 'rgba(33, 150, 243, 0.1)',
                border: '1px solid #2196F3',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Important Information
              </Typography>
              <Typography variant="body2" component="div">
                • Once submitted, this request cannot be easily cancelled<br />
                • Payment is subject to availability of liquidity (Clause 4.5)<br />
                {redemptionType === 'full' ? (
                  <>
                    • Your series will be fully redeemed and closed<br />
                    • No further crystallizations will occur for this series<br />
                  </>
                ) : (
                  <>
                    • Your series will remain ACTIVE after this {redemptionPercentage}% redemption<br />
                    • You can request additional redemptions in the future<br />
                    • Crystallizations will continue on the remaining balance<br />
                  </>
                )}
                • Performance fees are calculated on the redeemed portion only<br />
              </Typography>
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #333', p: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{ color: 'white' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading || !calculation || activeSeries.length === 0}
          variant="contained"
          sx={{
            bgcolor: '#FFD700',
            color: '#000',
            '&:hover': { bgcolor: '#FFC700' },
          }}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: '#000' }} />
          ) : (
            'Submit Redemption Request'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequestRedemptionDialog;
